import React, { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DropZoneUpload } from "@/components/ui/drop-zone-upload";
import { Check, Loader2, ImageIcon, Search } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { uploadImageToStorage } from "@/lib/world-helpers";
import { logActivity } from "@/lib/activity-logger";
import type { ImageGenTabProps, MediaItem } from "./types";

export function LibraryTab(props: ImageGenTabProps) {
  const {
    entity, worldId, existingMedia = [], currentImageUrl,
    onImageAccepted, onImageGenerated, onOpenChange, imageType,
  } = props;

  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [extraMedia, setExtraMedia] = useState<MediaItem[]>([]);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [fetchedMedia, setFetchedMedia] = useState<MediaItem[]>([]);
  const [fetching, setFetching] = useState(false);

  const allMedia = useMemo(() => {
    const merged = [...extraMedia, ...existingMedia, ...fetchedMedia];
    const seen = new Set<string>();
    return merged.filter(m => {
      if (seen.has(m.image_url)) return false;
      seen.add(m.image_url);
      return true;
    });
  }, [extraMedia, existingMedia, fetchedMedia]);

  // Auto-fetch world media on mount so Library is always populated
  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    supabase
      .from("world_media")
      .select("id, image_url, title, type")
      .eq("world_id", worldId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false })
      .limit(200)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { console.error("LibraryTab fetch error:", error); }
        else if (data) { setFetchedMedia(data as MediaItem[]); }
        setFetching(false);
      });
    return () => { cancelled = true; };
  }, [worldId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allMedia;
    return allMedia.filter(m =>
      (m.title || "").toLowerCase().includes(q) ||
      (m.type || "").toLowerCase().includes(q)
    );
  }, [allMedia, query]);

  const acceptImage = (url: string) => {
    setAccepting(url);
    if (onImageAccepted) onImageAccepted(url);
    else onImageGenerated?.(entity.id, url);
    toast.success(`Image set for ${entity.name}`);
    onOpenChange(false);
  };

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }
    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const imageUrl = await uploadImageToStorage(file, worldId);
      const { data: inserted, error } = await supabase
        .from("world_media")
        .insert({
          world_id: worldId,
          entity_id: entity.id || null,
          type: imageType || "scene",
          title: file.name.replace(/\.[^.]+$/, "") || `Upload — ${entity.name}`,
          description: "Uploaded via Media Studio",
          image_url: imageUrl,
          prompt_used: "user-upload",
          created_by: user.id,
        })
        .select("id, image_url, title, type")
        .single();
      if (error) throw error;
      const newItem: MediaItem = inserted as MediaItem;
      setExtraMedia(prev => [newItem, ...prev]);
      logActivity({
        action: `Uploaded image to library for "${entity.name}"`,
        category: "image_gen",
        source: "ImageGenerationDialog.LibraryTab",
        details: { entity_id: entity.id, type: imageType },
      });
      toast.success("Uploaded to library");
    } catch (err: any) {
      toast.error("Upload failed: " + (err.message || err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-xs font-semibold text-foreground">Upload to Library</label>
        <DropZoneUpload
          onFileSelected={handleUpload}
          accept="image/*"
          uploading={uploading}
          label="Drop, paste, or click to upload"
          sublabel="PNG, JPG, WebP — saved to this world's media library"
          validateFile={(f) => f.type.startsWith("image/") ? null : "Only image files are supported"}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5 text-primary" />
            Library
            <Badge variant="secondary" className="text-[9px] h-4 px-1.5">{allMedia.length}</Badge>
          </label>
          {allMedia.length > 0 && (
            <div className="relative w-44">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                className="h-7 pl-7 text-xs"
              />
            </div>
          )}
        </div>

        {fetching && allMedia.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center">
            <Loader2 className="w-6 h-6 text-muted-foreground mx-auto mb-2 animate-spin" />
            <p className="text-xs text-muted-foreground">Loading media library…</p>
          </div>
        ) : allMedia.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center">
            <ImageIcon className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No images in the library yet — upload one above.</p>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground italic px-1 py-3">No matches for "{query}"</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {filtered.map((m) => {
              const isCurrent = currentImageUrl === m.image_url || entity.image_url === m.image_url;
              const isAccepting = accepting === m.image_url;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => acceptImage(m.image_url)}
                  disabled={!!accepting}
                  className={cn(
                    "group relative aspect-square rounded-md overflow-hidden border-2 transition-all text-left",
                    isCurrent
                      ? "border-primary ring-1 ring-primary/30"
                      : "border-border hover:border-primary/50 hover:shadow-sm",
                    accepting && !isAccepting && "opacity-50",
                  )}
                  title={m.title}
                >
                  <img
                    src={m.image_url}
                    alt={m.title}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-1">
                    <p className="text-[10px] text-white font-medium truncate">{m.title}</p>
                    {m.type && (
                      <p className="text-[9px] text-white/70 truncate capitalize">{m.type}</p>
                    )}
                  </div>
                  {isCurrent && (
                    <div className="absolute top-1 right-1 bg-primary rounded-full p-0.5">
                      <Check className="w-2.5 h-2.5 text-primary-foreground" />
                    </div>
                  )}
                  {isAccepting && (
                    <div className="absolute inset-0 bg-background/70 flex items-center justify-center">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}